// Zpětná vazba výživa v kartě klientky (1. 11. 2026, schválený mockup 6D).
// Seznam hodnocení nutričních konzultací z obvolávání (Hodnocení výživy, sales):
// datum konzultace, nutriční, kdo volal, hvězdy. Kliknutím detail vyplněného dotazníku.
// Vidí owner, CSM a sales (server /api/hodnoceni-vyzivy kontroluje roli), trenér ne.
//
// Použití: <ZpetnaVazbaVyzivaSection klientId={notion_id} onCount={(n) => ...} />
import React, { useEffect, useState } from "react";
import { ChevronRight, Soup, Star } from "lucide-react";
import { Modal, HlavickaOkna, SkeletonRows, ErrorNote } from "./ui.jsx";
import { Pil } from "./sales/salesUi.jsx";
import { OTAZKY_VYZIVA, popisOdpovedi, datumKratce, datumZCasu, krestni, hvGet } from "../lib/hodnoceniVyzivy.js";

function Hvezdy({ n }) {
  const v = Number(n) || 0;
  return (
    <span className="inline-flex shrink-0 items-center gap-0.5" aria-label={`${v} z 5 hvězd`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-4 w-4 ${i <= v ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
      ))}
    </span>
  );
}

function Detail({ h, onClose }) {
  const odp = h.odpovedi || {};
  return (
    <Modal open onClose={onClose} plnaHlavicka size="md"
      header={<HlavickaOkna ikona={Soup} nazev={`Konzultace ${datumKratce(h.konzultace_datum, true)}`}
        popis="Zpětná vazba z hovoru po nutriční konzultaci" onClose={onClose} />}>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {h.nutricni && <Pil ton="znacka" casti={h.nutricni} />}
          {h.volala && <Pil casti={[`Volala ${krestni(h.volala)}`, datumZCasu(h.hovor_at)]} />}
          {h.google_odkaz_at && <Pil ton="ok" casti={`Google odkaz poslán ${datumZCasu(h.google_odkaz_at)}`} />}
          {h.nevolat && <Pil ton="pozor" casti="Nevolat příště" />}
        </div>
        {h.stav === "nechce" || h.stav === "nedovolano" ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
            {h.stav === "nechce" ? "Klientka nechtěla hodnotit." : "Klientku se nepodařilo dovolat."}
          </div>
        ) : (
          <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
            {OTAZKY_VYZIVA.filter((o) => !o.jenKdyz || odp[o.jenKdyz.k] === o.jenKdyz.v || odp[o.k]).map((o) => (
              <div key={o.k} className="space-y-1 px-3 py-2.5 sm:px-4">
                <div className="text-[13px] font-semibold text-slate-500">{o.cislo}. {o.q}</div>
                {o.typ === "hvezdy"
                  ? <div className="flex items-center gap-2"><Hvezdy n={h.hvezdy} /><span className="text-sm font-bold text-slate-800">{h.hvezdy} z 5</span></div>
                  : <div className="whitespace-pre-line text-[15px] font-semibold text-slate-900 [overflow-wrap:anywhere]">{popisOdpovedi(o, odp[o.k]) || <span className="font-normal text-slate-400">bez odpovědi</span>}</div>}
                {o.pozn && odp[o.pozn] && <div className="whitespace-pre-line text-sm text-slate-600 [overflow-wrap:anywhere]">{odp[o.pozn]}</div>}
              </div>
            ))}
          </div>
        )}
        {h.poznamka && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
            <div className="text-[13px] font-bold text-amber-900">Interní poznámka</div>
            <div className="mt-0.5 whitespace-pre-line text-sm text-amber-900 [overflow-wrap:anywhere]">{h.poznamka}</div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function ZpetnaVazbaVyzivaSection({ klientId, onCount }) {
  const [seznam, setSeznam] = useState(null);
  const [chyba, setChyba] = useState("");
  const [detail, setDetail] = useState(null);

  const nacti = async () => {
    if (!klientId) { setSeznam([]); onCount?.(0); return; }
    try {
      const j = await hvGet("klientka", { klientId });
      setSeznam(j.hodnoceni || []); setChyba("");
      onCount?.((j.hodnoceni || []).length);
    } catch (e) { setChyba(e.message); }
  };
  useEffect(() => { nacti(); }, [klientId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (chyba) return <ErrorNote onRetry={nacti}>{chyba}</ErrorNote>;
  if (!seznam) return <SkeletonRows rows={2} />;
  if (!seznam.length) return <div className="text-sm text-slate-500">Zatím žádné hodnocení. Klientky po nutriční konzultaci obvolává sales v sekci Hodnocení výživy.</div>;

  return (
    <div className="space-y-2">
      {seznam.map((h) => (
        <button key={h.id} type="button" onClick={() => setDetail(h)}
          className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left transition sm:hover:bg-brand-50/50">
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-bold text-slate-900">Konzultace {datumKratce(h.konzultace_datum, true)}</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {h.nutricni && <Pil mala ton="znacka" casti={krestni(h.nutricni)} />}
              {h.volala && <Pil mala casti={[krestni(h.volala), datumZCasu(h.hovor_at)]} />}
              {h.google_odkaz_at && <Pil mala ton="ok" casti={`Google odkaz poslán ${datumZCasu(h.google_odkaz_at)}`} />}
            </div>
          </div>
          {h.stav === "nechce" ? <Pil mala casti="Nechce hodnotit" /> : h.stav === "nedovolano" ? <Pil mala casti="Nedovoláno" /> : <Hvezdy n={h.hvezdy} />}
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
        </button>
      ))}
      {detail && <Detail h={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
